import axios from "axios";
import { IdentifyingDocument } from "../types/professional";

const WEB_API = process.env.REACT_APP_API_URL;

const authHeaders = (authToken: string) => ({
  headers: { Authorization: `Bearer ${authToken}` }
});

/* -------------------------------------------------------------------------
 * Image storage
 * ---------------------------------------------------------------------- */

/**
 * Uploads an image and answers the URL it can be read back from.
 *
 * Two steps: the API hands out a pre-signed URL, the file goes straight to
 * storage, and the public address is what gets stored on the document.
 *
 * The answer is read tolerantly on purpose. The mobile app, which has been
 * calling this in production for years, reads `{ signedUrl, publicUrl }`, while
 * the OpenAPI document describes `{ url, key, validFor }`. Rather than bet on
 * either, both spellings are accepted; and if no public address is given, it is
 * taken to be the signed URL without its query string, which for S3 is the
 * object's own address.
 */
export const uploadPicture = async ({
  authToken,
  file
}: {
  authToken: string;
  file: File;
}): Promise<string> => {
  const { data } = await axios.get(
    `${WEB_API}/api/storage/uploadPicture`,
    authHeaders(authToken)
  );

  const signedUrl: string | undefined = data?.signedUrl ?? data?.url;
  if (!signedUrl) throw new Error("No upload URL was returned");

  await axios.put(signedUrl, file, {
    headers: {
      // Storage keeps the object private unless it is told otherwise, and these
      // images are read back by <img> tags with no credentials attached.
      "x-amz-acl": "public-read",
      "Content-Type": file.type
    }
  });

  return data?.publicUrl ?? signedUrl.split("?")[0];
};

/* -------------------------------------------------------------------------
 * Identifying documents
 * ---------------------------------------------------------------------- */

export interface CreateIdentifyingDocumentInput {
  professional: boolean;
  type: number;
  documentId: string;
  front: string;
  back: string;
  /** ISO 8601. Omitted rather than sent empty when unknown. */
  expirationDate?: string;
}

/**
 * Registers a document.
 *
 * This is also how a document is renewed: the expiry date cannot be rewritten,
 * so a new card is filed rather than the old one amended, and the previous one
 * remains as history.
 */
export const createIdentifyingDocument = async ({
  authToken,
  input
}: {
  authToken: string;
  input: CreateIdentifyingDocumentInput;
}): Promise<IdentifyingDocument> => {
  const { data } = await axios.post(
    `${WEB_API}/api/professionals/identifying-documents`,
    input,
    authHeaders(authToken)
  );
  return data?.identifyingDocument ?? data;
};

/**
 * Fills in the expiry date of a document that has none.
 *
 * Write-once: a document that already carries a date answers 400. Only useful
 * for the records signup created without one.
 */
export const setIdentifyingDocumentExpiration = async ({
  authToken,
  documentId,
  expirationDate
}: {
  authToken: string;
  documentId: string;
  expirationDate: string;
}): Promise<IdentifyingDocument> => {
  const { data } = await axios.patch(
    `${WEB_API}/api/professionals/identifying-documents/${documentId}`,
    { expirationDate },
    authHeaders(authToken)
  );
  return data?.identifyingDocument ?? data;
};

/* -------------------------------------------------------------------------
 * Specialty and contact numbers
 * ---------------------------------------------------------------------- */

/** `occupation` itself is immutable — sending it is rejected, not ignored. */
export const updateOccupationSpecialty = async ({
  authToken,
  occupationSpecialty
}: {
  authToken: string;
  occupationSpecialty: number;
}) => {
  const { data } = await axios.patch(
    `${WEB_API}/api/professionals/occupation-specialty`,
    { occupationSpecialty },
    authHeaders(authToken)
  );
  return data;
};

export interface PhoneNumberInput {
  /** ISO 3166-1 alpha-2, never a dialling code — "+351" is rejected. */
  countryPrefix: string;
  /** National number for that country, without a leading "+". */
  number: string;
}

/**
 * Both numbers are update-only: they are registered at signup, and a
 * professional without one gets a 404 rather than having one created.
 *
 * The CMD number must be a mobile, since the signing code arrives by SMS; the
 * practice number may be a landline.
 */
export const updateCmdPhone = async ({
  authToken,
  phone
}: {
  authToken: string;
  phone: PhoneNumberInput;
}) => {
  const { data } = await axios.patch(
    `${WEB_API}/api/professionals/cmd-phone`,
    phone,
    authHeaders(authToken)
  );
  return data;
};

export const updateProfessionalPhone = async ({
  authToken,
  phone
}: {
  authToken: string;
  phone: PhoneNumberInput;
}) => {
  const { data } = await axios.patch(
    `${WEB_API}/api/professionals/professional-phone`,
    phone,
    authHeaders(authToken)
  );
  return data;
};

/* -------------------------------------------------------------------------
 * Emails, personal phones and addresses
 *
 * The three lists a professional keeps on their personal information. Each is
 * add + remove only — there is no update route for an email or an address, and
 * the server enforces which entries may go:
 *
 *   - the first email listed is the primary one and can never be removed;
 *   - a professional always keeps at least one personal phone;
 *   - a professional always keeps at least one practice address.
 *
 * Breaking any of those answers 400 with a `msg` explaining which; `serverMessage`
 * pulls it out so the screen can say what the server said rather than guess.
 * ---------------------------------------------------------------------- */

/** The `{ msg }` an API rejection carries, when there is one. */
export const serverMessage = (error: unknown): string | undefined => {
  const msg = (error as any)?.response?.data?.msg;
  return typeof msg === "string" && msg.trim() ? msg : undefined;
};

export interface ProfessionalEmail {
  _id: string;
  value: string;
  personal?: boolean;
  /** Present, and true, only on the primary email. */
  main?: boolean;
}

export interface PersonalPhone {
  _id: string;
  number: string;
  countryPrefix: string;
  personal?: boolean;
}

export interface ProfessionalAddress {
  _id: string;
  typeAddress: number;
  name?: string;
  street?: string;
  city?: string;
  postalCode?: string;
  /** ISO 3166-1 alpha-2. */
  country?: string;
  /** True for a practice address, false for a private one. */
  professional?: boolean;
}

/** Only these two types may be chosen for a new address; older rows keep theirs. */
export const CREATABLE_ADDRESS_TYPES = [3, 6] as const;
export type CreatableAddressType = typeof CREATABLE_ADDRESS_TYPES[number];
export const ADDRESS_TYPE_CLINIC: CreatableAddressType = 3;
export const ADDRESS_TYPE_DOMICILE: CreatableAddressType = 6;

export interface CreateAddressInput {
  typeAddress: CreatableAddressType;
  /** Required for CLINIC; ignored by the server for DOMICILE. */
  name?: string;
  street: string;
  city: string;
  postalCode: string;
  /** ISO 3166-1 alpha-2 — a country name or dialling code is rejected. */
  country: string;
  professional: boolean;
}

/** The address is stored trimmed and lower-cased, and starts unvalidated. */
export const addEmail = async ({
  authToken,
  value,
  personal = true
}: {
  authToken: string;
  value: string;
  personal?: boolean;
}): Promise<ProfessionalEmail> => {
  const { data } = await axios.post(
    `${WEB_API}/api/professionals/emails`,
    { value, personal },
    authHeaders(authToken)
  );
  return data?.email ?? data;
};

export const removeEmail = async ({
  authToken,
  emailId
}: {
  authToken: string;
  emailId: string;
}): Promise<void> => {
  await axios.delete(
    `${WEB_API}/api/professionals/emails/${emailId}`,
    authHeaders(authToken)
  );
};

/** Unlike the CMD number, a landline is perfectly valid here. */
export const addPersonalPhone = async ({
  authToken,
  phone
}: {
  authToken: string;
  phone: PhoneNumberInput;
}): Promise<PersonalPhone> => {
  const { data } = await axios.post(
    `${WEB_API}/api/professionals/personal-phones`,
    phone,
    authHeaders(authToken)
  );
  return data?.personalPhone ?? data;
};

export const removePersonalPhone = async ({
  authToken,
  personalPhoneId
}: {
  authToken: string;
  personalPhoneId: string;
}): Promise<void> => {
  await axios.delete(
    `${WEB_API}/api/professionals/personal-phones/${personalPhoneId}`,
    authHeaders(authToken)
  );
};

export const addAddress = async ({
  authToken,
  input
}: {
  authToken: string;
  input: CreateAddressInput;
}): Promise<ProfessionalAddress> => {
  const { data } = await axios.post(
    `${WEB_API}/api/professionals/addresses`,
    input,
    authHeaders(authToken)
  );
  return data?.address ?? data;
};

export const removeAddress = async ({
  authToken,
  addressId
}: {
  authToken: string;
  addressId: string;
}): Promise<void> => {
  await axios.delete(
    `${WEB_API}/api/professionals/addresses/${addressId}`,
    authHeaders(authToken)
  );
};
